import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Fade,
  Avatar,
  Snackbar,
  Alert,
  TablePagination,
  Paper
} from '@mui/material';
import Fab from '@mui/material/Fab';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import StoreIcon from '@mui/icons-material/Store';
import HistoryIcon from '@mui/icons-material/History';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import AddIcon from '@mui/icons-material/Add';
import LockResetIcon from '@mui/icons-material/LockReset';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/fr';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { superadminService } from '../../../services/superadmin.service';
import auditService from '../../../services/audit.service';
import { theme } from '../../../theme';
import ProfessionalCard from '../components/ProfessionalCard';

moment.locale('fr');

function useQueryParam(name) {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search).get(name) || '', [search, name]);
}

const ProfessionalsList = () => {
  const navigate = useNavigate();
  const urlSearch = useQueryParam('search');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const rows = useMemo(() => items || [], [items]);

  const [search, setSearch] = useState(urlSearch || '');
  const [submittedSearch, setSubmittedSearch] = useState(urlSearch || '');

  const [viewMode, setViewMode] = useState('list');
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });

  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');
  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', email: '', phone: '', is_active: true });

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyItems, setHistoryItems] = useState([]);
  const [historyUser, setHistoryUser] = useState(null);

  const [resetOpen, setResetOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetUser, setResetUser] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteUser, setDeleteUser] = useState(null);
  const [archivedOnly, setArchivedOnly] = useState(false);

  // Toast State
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const [selectedIds, setSelectedIds] = useState([]);

  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyError, setNotifyError] = useState('');
  const [closureStart, setClosureStart] = useState('');
  const [closureEnd, setClosureEnd] = useState('');
  const [closureReason, setClosureReason] = useState('');

  const [campaignTemplateKey, setCampaignTemplateKey] = useState('custom');

  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templatesSelectedKey, setTemplatesSelectedKey] = useState('closure_exceptional');

  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [subscriptionPlanCode, setSubscriptionPlanCode] = useState('');
  const [subscriptionNote, setSubscriptionNote] = useState('');

  const selectedSubscriptionPlan = useMemo(() => {
    if (!subscriptionPlanCode) return null;
    return subscriptionPlans.find((p) => p?.code === subscriptionPlanCode) || null;
  }, [subscriptionPlanCode, subscriptionPlans]);

  const [sendEmail, setSendEmail] = useState(true);
  const [sendNotification, setSendNotification] = useState(true);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailHtml, setEmailHtml] = useState('');
  const [emailHtmlToSend, setEmailHtmlToSend] = useState('');
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const allSelected = rows.length > 0 && selectedIds.length === rows.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < rows.length;

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (rows.length === 0) return prev;
      return prev.length === rows.length ? [] : rows.map((p) => p.id);
    });
  };

  const openNotifyDialog = () => {
    if (selectedIds.length === 0) {
      showToast('Sélectionnez au moins un responsable.', 'warning');
      return;
    }
    setNotifyError('');
    setClosureStart('');
    setClosureEnd('');
    setClosureReason('');
    setCampaignTemplateKey('custom');
    setSubscriptionPlanCode('');
    setSubscriptionNote('');
    setSendEmail(true);
    setSendNotification(true);
    setEmailSubject('');
    setNotificationTitle('');
    setNotificationMessage('');
    setEmailHtml('');
    setEmailHtmlToSend('');
    setNotifyOpen(true);
  };

  useEffect(() => {
    if (!templatesOpen) return;
    if (templatesSelectedKey !== 'subscription_renewal') return;

    const loadPlans = async () => {
      try {
        const res = await superadminService.listSubscriptionPlans({ page_size: 250 });
        const data = res?.data;
        const items = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
        setSubscriptionPlans(items);
      } catch (_e) {
        setSubscriptionPlans([]);
      }
    };
    loadPlans();
  }, [templatesOpen, templatesSelectedKey]);

  const buildEmailHtml = useCallback(({ title, bodyLines, ctaText }) => {
    const safeLines = Array.isArray(bodyLines) ? bodyLines : [];
    const bodyHtml = safeLines.map((l) => `<p style="margin:0 0 12px 0;color:#111827;font-size:14px;line-height:20px;">${l}</p>`).join('');
    const ctaHtml = ctaText
      ? `<div style="margin-top:18px;padding:12px 14px;border-radius:10px;background:#F8FAFC;border:1px solid #E2E8F0;color:#0F172A;font-size:13px;line-height:18px;">${ctaText}</div>`
      : '';

    return (
      '<div style="font-family:Inter,Arial,sans-serif;background:#F8FAFC;padding:24px;">'
      + '<div style="max-width:640px;margin:0 auto;background:#ffffff;border:1px solid #E2E8F0;border-radius:14px;overflow:hidden;">'
      + `<div style="padding:18px 20px;background:linear-gradient(90deg,#16A34A,#22C55E);color:white;">`
      + `<div style="font-size:14px;opacity:0.9;">Saloone</div>`
      + `<div style="font-size:18px;font-weight:800;margin-top:2px;">${title}</div>`
      + '</div>'
      + '<div style="padding:18px 20px;">'
      + bodyHtml
      + ctaHtml
      + '<div style="margin-top:18px;color:#64748B;font-size:12px;line-height:18px;">Cet email a été envoyé par l\'administration.</div>'
      + '</div>'
      + '</div>'
      + '</div>'
    );
  }, []);

  const wrapEmailHtml = useCallback(({ title, innerHtml }) => {
    const safeTitle = (title || '').trim() || 'Message';
    const safeInner = (innerHtml || '').trim();
    return (
      '<div style="font-family:Inter,Arial,sans-serif;background:#F8FAFC;padding:24px;">'
      + '<div style="max-width:640px;margin:0 auto;background:#ffffff;border:1px solid #E2E8F0;border-radius:14px;overflow:hidden;">'
      + `<div style="padding:18px 20px;background:linear-gradient(90deg,#16A34A,#22C55E);color:white;">`
      + `<div style="font-size:14px;opacity:0.9;">Saloone</div>`
      + `<div style="font-size:18px;font-weight:800;margin-top:2px;">${safeTitle}</div>`
      + '</div>'
      + '<div style="padding:18px 20px;">'
      + safeInner
      + '<div style="margin-top:18px;color:#64748B;font-size:12px;line-height:18px;">Cet email a été envoyé par l\'administration.</div>'
      + '</div>'
      + '</div>'
      + '</div>'
    );
  }, []);

  const getTemplateDraft = useCallback((key) => {
    if (key === 'closure_exceptional') {
      const startLabel = closureStart ? new Date(closureStart).toLocaleString() : '...';
      const endLabel = closureEnd ? new Date(closureEnd).toLocaleString() : '...';
      const reasonLine = closureReason ? `Motif: ${closureReason}` : '';
      const notifTitle = 'Fermeture exceptionnelle';
      const notifMessage = `Fermeture demandée du ${startLabel} au ${endLabel}. ${reasonLine}`.trim();
      const emailTitle = 'Demande de fermeture exceptionnelle';
      const emailSubject = emailTitle;
      const editorHtml = (
        '<p>Bonjour,</p>'
        + `<p>Une fermeture exceptionnelle est demandée du ${startLabel} au ${endLabel}.</p>`
        + (closureReason ? `<p>Motif: ${closureReason}</p>` : '')
        + "<p>Merci de confirmer dans l'application (notification Oui / Non).</p>"
      );
      const emailHtml = buildEmailHtml({
        title: emailTitle,
        bodyLines: [
          "Bonjour,",
          `Une fermeture exceptionnelle est demandée du ${startLabel} au ${endLabel}.`,
          ...(closureReason ? [`Motif: ${closureReason}`] : []),
        ],
        ctaText: "Merci de confirmer dans l'application (notification Oui / Non).",
      });
      return { notifTitle, notifMessage, emailSubject, emailHtml, editorHtml, templateKey: 'closure_exceptional' };
    }

    if (key === 'subscription_renewal') {
      const planLabel = (selectedSubscriptionPlan?.name || subscriptionPlanCode) ? (selectedSubscriptionPlan?.name || subscriptionPlanCode) : '...';
      const notifTitle = 'Renouvellement abonnement';
      const notifMessage = `Votre renouvellement est prêt pour le plan ${planLabel}. Merci de suivre les instructions envoyées par email.`;
      const emailTitle = 'Renouvellement abonnement';
      const emailSubject = emailTitle;
      const editorHtml = (
        '<p>Bonjour,</p>'
        + `<p>Votre renouvellement d'abonnement est disponible pour le plan ${planLabel}.</p>`
        + (subscriptionNote ? `<p>${subscriptionNote}</p>` : '')
        + '<p>Si vous avez des questions, répondez à cet email ou contactez l\'assistance.</p>'
      );
      const emailHtml = buildEmailHtml({
        title: emailTitle,
        bodyLines: [
          "Bonjour,",
          `Votre renouvellement d'abonnement est disponible pour le plan ${planLabel}.`,
          ...(subscriptionNote ? [subscriptionNote] : []),
        ],
        ctaText: "Si vous avez des questions, répondez à cet email ou contactez l'assistance.",
      });
      return { notifTitle, notifMessage, emailSubject, emailHtml, editorHtml, templateKey: 'subscription_renewal' };
    }

    return {
      notifTitle: 'Message',
      notifMessage: '',
      emailSubject: 'Message',
      editorHtml: '<p>Bonjour,</p>',
      emailHtml: buildEmailHtml({ title: 'Message', bodyLines: ["Bonjour,"], ctaText: '' }),
      templateKey: 'custom',
    };
  }, [buildEmailHtml, closureEnd, closureReason, closureStart, subscriptionNote, subscriptionPlanCode]);

  const applySelectedTemplate = () => {
    const d = getTemplateDraft(templatesSelectedKey);
    setCampaignTemplateKey(d.templateKey);
    setNotificationTitle(d.notifTitle);
    setNotificationMessage(d.notifMessage);
    setEmailSubject(d.emailSubject);
    setEmailHtml(d.editorHtml || d.emailHtml);
    setEmailHtmlToSend(d.emailHtml);
    setTemplatesOpen(false);
  };

  const submitNotify = async () => {
    try {
      setNotifyError('');

      if (campaignTemplateKey === 'closure_exceptional') {
        if (!closureStart || !closureEnd) {
          setNotifyError('Veuillez renseigner la date/heure de début et de fin.');
          return;
        }
      }
      if (campaignTemplateKey === 'subscription_renewal') {
        if (!subscriptionPlanCode) {
          setNotifyError('Veuillez sélectionner un plan.');
          return;
        }
      }

      if (!sendEmail && !sendNotification) {
        setNotifyError('Veuillez sélectionner au moins un canal (Email ou Notification).');
        return;
      }
      setNotifyLoading(true);

      const payload =
        campaignTemplateKey === 'closure_exceptional'
          ? {
              start_datetime: closureStart,
              end_datetime: closureEnd,
              reason: closureReason,
            }
          : campaignTemplateKey === 'subscription_renewal'
          ? {
              plan_code: subscriptionPlanCode,
              note: subscriptionNote,
            }
          : {};

      await superadminService.sendCampaign({
        user_ids: selectedIds,
        template_key: campaignTemplateKey,
        channels: { email: sendEmail, notification: sendNotification },
        payload,
        overrides: {
          email_subject: emailSubject,
          email_html: (emailHtmlToSend || '').trim() || emailHtml,
          notification_title: notificationTitle,
          notification_message: notificationMessage,
        },
      });
      showToast('Notification envoyée.', 'success');
      setNotifyOpen(false);
      setSelectedIds([]);
    } catch (e) {
      setNotifyError(e?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setToast({ ...toast, open: false });
  };

  const load = async (termOverride) => {
    try {
      setError('');
      setLoading(true);
      const term = typeof termOverride === 'string' ? termOverride : submittedSearch;
      const res = await superadminService.listProfessionals({
        search: term,
        page: page + 1,
        page_size: pageSize,
        include_archived: archivedOnly ? 1 : 0,
        archived_only: archivedOnly ? 1 : 0,
      });
      const data = res?.data;
      if (data?.results) {
        setItems(data.results);
        setTotal(data.count || 0);
      } else {
        setItems(data || []);
        setTotal(Array.isArray(data) ? data.length : 0);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openReset = (p) => {
    setResetError('');
    setResetSuccess('');
    setResetUser(p);
    setResetOpen(true);
  };

  const openDelete = (p) => {
    setDeleteError('');
    setDeleteUser(p);
    setDeleteOpen(true);
  };

  const handleArchive = async () => {
    if (!deleteUser?.id) return;
    try {
      setDeleteError('');
      setDeleteLoading(true);
      await superadminService.archiveProfessional(deleteUser.id);
      showToast('Responsable archivé.', 'success');
      setDeleteOpen(false);
      setDeleteUser(null);
      await load(submittedSearch);
    } catch (e) {
      setDeleteError(e?.response?.data?.detail || "Erreur lors de l'archivage");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!deleteUser?.id) return;
    try {
      setDeleteError('');
      setDeleteLoading(true);
      await superadminService.restoreProfessional(deleteUser.id);
      showToast('Responsable restauré.', 'success');
      setDeleteOpen(false);
      setDeleteUser(null);
      await load(submittedSearch);
    } catch (e) {
      setDeleteError(e?.response?.data?.detail || 'Erreur lors de la restauration');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePurge = async () => {
    if (!deleteUser?.id) return;
    try {
      setDeleteError('');
      setDeleteLoading(true);
      await superadminService.purgeProfessional(deleteUser.id);
      showToast('Responsable supprimé définitivement.', 'success');
      setDeleteOpen(false);
      setDeleteUser(null);
      await load(submittedSearch);
    } catch (e) {
      setDeleteError(e?.response?.data?.detail || 'Erreur lors de la suppression définitive');
    } finally {
      setDeleteLoading(false);
    }
  };

  const submitReset = async () => {
    if (!resetUser?.id) return;
    try {
      setResetError('');
      setResetSuccess('');
      setResetLoading(true);
      const res = await superadminService.resetUserPassword(resetUser.id);
      showToast(res?.data?.detail || res?.data?.message || 'Demande de réinitialisation envoyée.', 'success');
      setResetOpen(false);
    } catch (e) {
      setResetError(e?.response?.data?.detail || 'Erreur lors de la demande de réinitialisation');
    } finally {
      setResetLoading(false);
    }
  };

  const openHistory = async (p) => {
    setHistoryError('');
    setHistoryItems([]);
    setHistoryUser(p);
    setHistoryOpen(true);
    try {
      setHistoryLoading(true);
      const res = await auditService.getUserLogs(p.id, { page_size: 25 });
      setHistoryItems(res?.data?.results || []);
    } catch (e) {
      setHistoryError(e?.response?.data?.detail || "Erreur lors du chargement de l'historique");
    } finally {
      setHistoryLoading(false);
    }
  };

  const openEdit = (p) => {
    setEditError('');
    setEditSuccess('');
    setSelected(p);
    setEditForm({
      first_name: p?.first_name || '',
      last_name: p?.last_name || '',
      email: p?.email || '',
      phone: p?.phone || '',
      is_active: p?.is_active ?? true,
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!selected?.id) return;
    try {
      setEditError('');
      setEditSuccess('');
      setEditLoading(true);
      const payload = {
        first_name: editForm.first_name.trim(),
        last_name: editForm.last_name.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone.trim(),
        is_active: !!editForm.is_active,
      };
      await superadminService.updateProfessional(selected.id, payload);
      showToast('Modifications enregistrées.', 'success');
      setEditOpen(false);
      await load(submittedSearch);
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.email?.[0] ||
        e?.response?.data?.first_name?.[0] ||
        e?.response?.data?.last_name?.[0] ||
        e?.response?.data?.phone?.[0] ||
        'Erreur lors de la sauvegarde';
      setEditError(msg);
    } finally {
      setEditLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [submittedSearch, page, pageSize, archivedOnly]);

  const submitCreate = async () => {
    try {
      setCreateError('');
      setCreateSuccess('');
      setCreateLoading(true);
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      };
      const res = await superadminService.createResponsable(payload);
      const data = res?.data;
      let msg = '';
      if (data?.email_sent) {
        msg = `Responsable créé. Email envoyé à ${data.email}.`;
      } else {
        msg = `Responsable créé (${data?.email || payload.email}). Email non envoyé.`;
      }
      showToast(msg, 'success');
      setCreateOpen(false);
      await load(submittedSearch);
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.email?.[0] ||
        e?.response?.data?.first_name?.[0] ||
        e?.response?.data?.last_name?.[0] ||
        'Erreur lors de la création';
      setCreateError(msg);
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', pb: 12 }}>
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 4,
          flexWrap: 'wrap'
        }}
      >
        <Box sx={{ mb: { xs: 0.5, md: 0 } }}>
          <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.25rem', md: '1.75rem' }, color: theme.colors.text.primary, mb: 0.25 }}>
            Professionnels
          </Typography>
          <Typography variant="body2" sx={{ color: theme.colors.text.secondary, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Gérez les comptes responsables des salons
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            onClick={openNotifyDialog}
            disabled={rows.length === 0}
            sx={{
              borderRadius: theme.borderRadius.lg,
              textTransform: 'none',
              fontWeight: 800,
              backgroundColor: theme.colors.primary[600],
              '&:hover': { backgroundColor: theme.colors.primary[700] },
            }}
          >
            Notifier
          </Button>

          <Button
            variant={archivedOnly ? 'outlined' : 'text'}
            color={archivedOnly ? 'warning' : 'inherit'}
            onClick={() => {
              setPage(0);
              setArchivedOnly((prev) => !prev);
            }}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {archivedOnly ? 'Voir actifs' : 'Voir archivés'}
          </Button>

          {/* View Toggle */}
          <ToggleButtonGroup
            exclusive
            value={viewMode}
            onChange={(_, v) => {
              if (v) setViewMode(v);
            }}
            size="small"
            sx={{
              bgcolor: 'white',
              border: `1px solid ${theme.colors.border.light}`,
              borderRadius: theme.borderRadius.lg,
              height: { xs: 36, sm: 48 },
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: theme.borderRadius.lg,
                px: { xs: 1, sm: 2 }
              }
            }}
          >
            <ToggleButton value="list">
              <ViewListIcon fontSize="small" sx={{ mr: { xs: 0, sm: 1 } }} />
              <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 700 }}>LISTE</Typography>
            </ToggleButton>
            <ToggleButton value="card">
              <ViewModuleIcon fontSize="small" sx={{ mr: { xs: 0, sm: 1 } }} />
              <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 700 }}>GRILLE</Typography>
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Ajouter un responsable">
            <Fab
              color="primary"
              size="small"
              onClick={() => {
                setCreateSuccess('');
                setCreateError('');
                setForm({ first_name: '', last_name: '', email: '', phone: '' });
                setCreateOpen(true);
              }}
              sx={{
                width: { xs: 36, sm: 44 },
                height: { xs: 36, sm: 44 },
                boxShadow: '0 4px 12px rgba(22, 163, 74, 0.2)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(22, 163, 74, 0.3)',
                }
              }}
            >
              <AddIcon fontSize="small" />
            </Fab>
          </Tooltip>
        </Box>
      </Box>

      {/* Search Bar */}
      <Card
        sx={{
          borderRadius: theme.borderRadius.xl,
          border: 'none',
          boxShadow: theme.boxShadow.sm,
          backgroundColor: '#fff',
          mb: 4,
          overflow: 'visible'
        }}
      >
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              setSubmittedSearch(search.trim());
            }}
            sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
          >
            <Box sx={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}>
              <SearchIcon sx={{ color: theme.colors.text.tertiary, fontSize: 22, ml: 2, mr: 1.5 }} />
              <TextField
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par nom, email, téléphone..."
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  style: { fontSize: '0.95rem', fontWeight: 500 }
                }}
              />
              {search && (
                <IconButton size="small" onClick={() => { setSearch(''); setSubmittedSearch(''); load(''); }} sx={{ mr: 1 }}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Button
              type="submit"
              variant="contained"
              sx={{
                borderRadius: theme.borderRadius.lg,
                textTransform: 'none',
                fontWeight: 700,
                backgroundColor: theme.colors.text.primary,
                color: '#fff',
                px: { xs: 1.5, sm: 3 },
                my: 0.5,
                mr: 0.5,
                '&:hover': { backgroundColor: '#000' }
              }}
            >
              Rechercher
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Content Area */}
      {
        loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
            <CircularProgress sx={{ color: theme.colors.primary[500], mb: 2 }} />
            <Typography sx={{ color: theme.colors.text.secondary }}>Chargement des professionnels...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 8, backgroundColor: theme.colors.error[50], borderRadius: theme.borderRadius.xl }}>
            <Typography sx={{ color: theme.colors.error[600], fontWeight: 700 }}>{error}</Typography>
            <Button startIcon={<RefreshIcon />} onClick={() => load()}>Réessayer</Button>
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography sx={{ color: theme.colors.text.secondary, fontWeight: 500, fontSize: '1.1rem' }}>
              Aucun professionnel trouvé.
            </Typography>
          </Box>
        ) : viewMode === 'list' ? (
          <Fade in>
            <Card sx={{ borderRadius: theme.borderRadius.xl, overflow: 'hidden', boxShadow: theme.boxShadow.sm, border: `1px solid ${theme.colors.border.light}` }}>
              <Box sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: { xs: '100%', md: 800 } }}>
                  <TableHead sx={{ backgroundColor: theme.colors.background.secondary }}>
                    <TableRow>
                      <TableCell sx={{ width: 48, py: 2 }}>
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleSelectAll();
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: theme.colors.text.secondary, textTransform: 'uppercase', fontSize: '0.75rem', py: 2 }}>Nom</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: theme.colors.text.secondary, textTransform: 'uppercase', fontSize: '0.75rem', py: 2, display: { xs: 'none', md: 'table-cell' } }}>Contact</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: theme.colors.text.secondary, textTransform: 'uppercase', fontSize: '0.75rem', py: 2, display: { xs: 'none', md: 'table-cell' } }}>Dernière connexion</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: theme.colors.text.secondary, textTransform: 'uppercase', fontSize: '0.75rem', py: 2, display: { xs: 'none', md: 'table-cell' } }}>Activité</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: theme.colors.text.secondary, textTransform: 'uppercase', fontSize: '0.75rem', py: 2 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((p) => (
                      <TableRow
                        key={p.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                        }}
                        onClick={() => openEdit(p)}
                      >
                        <TableCell sx={{ py: { xs: 1.5, sm: 2.5 } }} onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.includes(p.id)}
                            onChange={() => toggleSelected(p.id)}
                          />
                        </TableCell>
                        <TableCell sx={{ py: { xs: 1.5, sm: 2.5 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                            <Avatar
                              sx={{
                                display: { xs: 'none', md: 'flex' },
                                width: { xs: 32, sm: 40 },
                                height: { xs: 32, sm: 40 },
                                bgcolor: theme.colors.secondary[100],
                                color: theme.colors.secondary[700],
                                fontWeight: 800,
                                fontSize: { xs: '0.75rem', sm: '0.9rem' }
                              }}
                            >
                              {p.first_name?.[0]}{p.last_name?.[0]}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 700, color: theme.colors.text.primary, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                                {p.first_name} {p.last_name}
                              </Typography>
                              <Chip
                                label={p.is_active ? 'Actif' : 'Inactif'}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  backgroundColor: p.is_active ? theme.colors.success[50] : theme.colors.grey[100],
                                  color: p.is_active ? theme.colors.success[700] : theme.colors.text.tertiary
                                }}
                              />
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2.5, display: { xs: 'none', md: 'table-cell' } }}>
                          <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>{p.email}</Typography>
                          {p.phone && <Typography variant="caption" sx={{ color: theme.colors.text.tertiary }}>{p.phone}</Typography>}
                        </TableCell>
                        <TableCell sx={{ py: 2.5, display: { xs: 'none', md: 'table-cell' } }}>
                          {p.last_login ? (
                            <Typography variant="body2" sx={{ color: theme.colors.text.secondary, fontWeight: 500 }}>
                              {moment(p.last_login).fromNow()}
                            </Typography>
                          ) : (
                            <Typography variant="caption" sx={{ color: theme.colors.text.tertiary }}>Jamais</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 2.5, display: { xs: 'none', md: 'table-cell' } }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip
                              size="small"
                              icon={<StoreIcon sx={{ fontSize: '1rem !important' }} />}
                              label={`${p.salons_count ?? 0}`}
                              sx={{ backgroundColor: theme.colors.primary[50], color: theme.colors.primary[700], fontWeight: 700, '& .MuiChip-icon': { color: theme.colors.primary[700] } }}
                            />
                            <Chip
                              size="small"
                              label={`${p.employees_count ?? 0} staff`}
                              sx={{ backgroundColor: theme.colors.background.tertiary, color: theme.colors.text.secondary, fontWeight: 600 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2.5 }}>
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Tooltip title="Éditer">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(p); }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Salons">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/superadmin/salons?search=${encodeURIComponent(p.email || '')}`); }}>
                                <StoreIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Historique">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); openHistory(p); }}>
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Réinitialiser mot de passe">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); openReset(p); }}>
                                <LockResetIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Supprimer">
                              <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); openDelete(p); }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Card>
          </Fade>
        ) : (
          <Fade in>
            <Grid container spacing={3}>
              {rows.map((p) => (
                <Grid key={p.id} item xs={12} sm={6} md={4} lg={3}>
                  <ProfessionalCard
                    user={p}
                    onEdit={openEdit}
                    onHistory={openHistory}
                    onReset={openReset}
                    onDelete={openDelete}
                    onViewSalons={() => navigate(`/superadmin/salons?search=${encodeURIComponent(p.email || '')}`)}
                  />
                </Grid>
              ))}
            </Grid>
          </Fade>
        )
      }

      {total > 0 && (
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          left: { xs: 0, md: '280px' },
          zIndex: 1000,
          p: 2,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none'
        }}>
          <Paper
            elevation={3}
            sx={{
              pointerEvents: 'auto',
              borderRadius: theme.borderRadius.xl,
              border: `1px solid ${theme.colors.border.light}`,
              overflow: 'hidden',
              width: 'fit-content'
            }}
          >
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => {
                setPage(newPage);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Lignes par page"
              sx={{ bgcolor: 'white' }}
            />
          </Paper>
        </Box>
      )}

      {/* Dialogs */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Ajouter un responsable</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {createError && <Typography sx={{ color: theme.colors.error[600], fontWeight: 800 }}>{createError}</Typography>}
            {createSuccess && <Typography sx={{ color: theme.colors.success[700], fontWeight: 800 }}>{createSuccess}</Typography>}
            <TextField
              label="Prénom"
              value={form.first_name}
              onChange={(e) => setForm((s) => ({ ...s, first_name: e.target.value }))}
              size="small"
              fullWidth
            />
            <TextField
              label="Nom"
              value={form.last_name}
              onChange={(e) => setForm((s) => ({ ...s, last_name: e.target.value }))}
              size="small"
              fullWidth
            />
            <TextField
              label="Email"
              value={form.email}
              onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
              size="small"
              fullWidth
            />
            <TextField
              label="Téléphone (optionnel)"
              value={form.phone}
              onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
              size="small"
              fullWidth
            />
            <Typography variant="caption" sx={{ color: theme.colors.text.tertiary }}>
              Le mot de passe est généré automatiquement et envoyé par email.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none', fontWeight: 800 }}>
            Fermer
          </Button>
          <Button
            onClick={submitCreate}
            disabled={createLoading}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 900 }}
          >
            {createLoading ? 'Création…' : 'Créer et envoyer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Détails du responsable</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {editError && <Typography sx={{ color: theme.colors.error[600], fontWeight: 800 }}>{editError}</Typography>}
            {editSuccess && <Typography sx={{ color: theme.colors.success[700], fontWeight: 800 }}>{editSuccess}</Typography>}
            <TextField
              label="Prénom"
              value={editForm.first_name}
              onChange={(e) => setEditForm((s) => ({ ...s, first_name: e.target.value }))}
              size="small"
              fullWidth
            />
            <TextField
              label="Nom"
              value={editForm.last_name}
              onChange={(e) => setEditForm((s) => ({ ...s, last_name: e.target.value }))}
              size="small"
              fullWidth
            />
            <TextField
              label="Email"
              value={editForm.email}
              onChange={(e) => setEditForm((s) => ({ ...s, email: e.target.value }))}
              size="small"
              fullWidth
            />
            <TextField
              label="Téléphone"
              value={editForm.phone}
              onChange={(e) => setEditForm((s) => ({ ...s, phone: e.target.value }))}
              size="small"
              fullWidth
            />
            <Chip
              label={editForm.is_active ? 'Actif' : 'Désactivé'}
              onClick={() => setEditForm((s) => ({ ...s, is_active: !s.is_active }))}
              size="small"
              sx={{
                alignSelf: 'flex-start',
                cursor: 'pointer',
                backgroundColor: editForm.is_active ? `${theme.colors.success[500]}25` : `${theme.colors.grey[300]}80`,
                color: editForm.is_active ? theme.colors.success[700] : theme.colors.text.secondary,
                fontWeight: 800,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} sx={{ textTransform: 'none', fontWeight: 800 }}>
            Fermer
          </Button>
          <Button
            onClick={submitEdit}
            disabled={editLoading}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 900 }}
          >
            {editLoading ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          Historique
          {historyUser ? ` — ${historyUser.first_name || ''} ${historyUser.last_name || ''}`.trim() : ''}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {historyError && <Typography sx={{ color: theme.colors.error[600], fontWeight: 800 }}>{historyError}</Typography>}
            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : historyItems.length === 0 ? (
              <Typography sx={{ color: theme.colors.text.secondary }}>
                Aucun historique.
              </Typography>
            ) : (
              <Table size="small" sx={{ mt: 1, '& .MuiTableCell-root': { borderBottomColor: theme.colors.border.light } }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.colors.grey[50] }}>
                    <TableCell sx={{ fontWeight: 700, color: theme.colors.text.secondary, fontSize: '0.78rem', py: 1.25 }}>
                      Action
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: theme.colors.text.secondary, fontSize: '0.78rem', py: 1.25 }}>
                      Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: theme.colors.text.secondary, fontSize: '0.78rem', py: 1.25 }}>
                      Détails
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyItems.map((log) => (
                    <TableRow key={log.id} hover sx={{ '&:hover': { backgroundColor: theme.colors.grey[50] } }}>
                      <TableCell>
                        <Chip
                          label={log.action_display || log.action}
                          size="small"
                          sx={{
                            backgroundColor: theme.colors.grey[100],
                            color: theme.colors.text.secondary,
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.9rem' }}>
                          {log.timestamp ? moment(log.timestamp).format('DD/MM/YYYY HH:mm') : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ color: theme.colors.text.primary, fontSize: '0.9rem' }}>
                          {log.description || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)} sx={{ textTransform: 'none', fontWeight: 800 }}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Réinitialisation du mot de passe</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography sx={{ color: theme.colors.text.secondary }}>
              Envoyer une demande de réinitialisation à
              {resetUser?.email ? ` ${resetUser.email}` : ''} ?
            </Typography>
            {resetError && <Typography sx={{ color: theme.colors.error[600], fontWeight: 800 }}>{resetError}</Typography>}
            {resetSuccess && <Typography sx={{ color: theme.colors.success[700], fontWeight: 800 }}>{resetSuccess}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)} sx={{ textTransform: 'none', fontWeight: 800 }}>
            Fermer
          </Button>
          <Button
            onClick={submitReset}
            disabled={resetLoading}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 900 }}
          >
            {resetLoading ? 'Envoi…' : 'Envoyer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => { if (!deleteLoading) { setDeleteOpen(false); setDeleteUser(null); } }} fullWidth maxWidth="xs">
        <DialogTitle>{deleteUser?.is_archived ? 'Gérer le responsable archivé' : 'Archiver le responsable'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography sx={{ color: theme.colors.text.secondary }}>
              {deleteUser?.is_archived
                ? 'Choisissez une action pour'
                : 'Confirmez-vous l’archivage de'}
              {deleteUser?.email ? ` ${deleteUser.email}` : ''} ?
            </Typography>
            {deleteError && <Typography sx={{ color: theme.colors.error[600], fontWeight: 800 }}>{deleteError}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteOpen(false); setDeleteUser(null); }} disabled={deleteLoading} sx={{ textTransform: 'none', fontWeight: 800 }}>
            Annuler
          </Button>
          {deleteUser?.is_archived ? (
            <>
              <Button onClick={handleRestore} disabled={deleteLoading} variant="outlined" sx={{ textTransform: 'none', fontWeight: 900 }}>
                {deleteLoading ? 'Restauration…' : 'Restaurer'}
              </Button>
              <Button onClick={handlePurge} disabled={deleteLoading} color="error" variant="contained" sx={{ textTransform: 'none', fontWeight: 900 }}>
                {deleteLoading ? 'Suppression…' : 'Supprimer définitivement'}
              </Button>
            </>
          ) : (
            <Button
              onClick={handleArchive}
              disabled={deleteLoading}
              color="warning"
              variant="contained"
              sx={{ textTransform: 'none', fontWeight: 900 }}
            >
              {deleteLoading ? 'Archivage…' : 'Archiver'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', fontWeight: 600, borderRadius: theme.borderRadius.md }} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>

      <Dialog open={notifyOpen} onClose={() => setNotifyOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Notifier</DialogTitle>
        <DialogContent>
          {notifyError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {notifyError}
            </Alert>
          )}

          <Typography variant="body2" sx={{ mb: 2, color: theme.colors.text.secondary }}>
            Responsables sélectionnés : {selectedIds.length}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography sx={{ fontWeight: 900 }}>Contenu</Typography>
              <Chip
                size="small"
                label={
                  campaignTemplateKey === 'custom'
                    ? 'Message libre'
                    : campaignTemplateKey === 'closure_exceptional'
                    ? 'Fermeture'
                    : 'Renouvellement'
                }
                sx={{ fontWeight: 800, bgcolor: 'rgba(22,163,74,0.08)', color: theme.colors.primary[700] }}
              />
            </Box>
            <Button
              onClick={() => {
                setTemplatesSelectedKey('closure_exceptional');
                setTemplatesOpen(true);
              }}
              sx={{ textTransform: 'none', fontWeight: 900 }}
            >
              Modèles
            </Button>
          </Box>

          <Typography sx={{ fontWeight: 800, mb: 1 }}>Canaux</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                />
              )}
              label="Email"
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={sendNotification}
                  onChange={(e) => setSendNotification(e.target.checked)}
                />
              )}
              label="Notification (cloche)"
            />
          </Box>

          {sendNotification && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 800, mb: 1 }}>Notification</Typography>
              <TextField
                fullWidth
                label="Titre"
                value={notificationTitle}
                onChange={(e) => setNotificationTitle(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Message"
                value={notificationMessage}
                onChange={(e) => setNotificationMessage(e.target.value)}
              />
            </Box>
          )}

          {sendEmail && (
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 800, mb: 1 }}>Email</Typography>
              <TextField
                fullWidth
                label="Objet"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <Box sx={{ border: `1px solid ${theme.colors.border.light}`, borderRadius: 2, overflow: 'hidden' }}>
                <ReactQuill value={emailHtml} onChange={setEmailHtml} theme="snow" />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyOpen(false)} disabled={notifyLoading}>
            Annuler
          </Button>
          <Button variant="contained" onClick={submitNotify} disabled={notifyLoading}>
            {notifyLoading ? 'Envoi...' : 'Envoyer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={templatesOpen} onClose={() => setTemplatesOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Modèles</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  border: `1px solid ${theme.colors.border.light}`,
                  borderRadius: theme.borderRadius.lg,
                  overflow: 'hidden',
                  backgroundColor: '#fff',
                }}
              >
                <List disablePadding>
                  <ListItemButton
                    selected={templatesSelectedKey === 'closure_exceptional'}
                    onClick={() => setTemplatesSelectedKey('closure_exceptional')}
                  >
                    <ListItemText
                      primary={<Typography sx={{ fontWeight: 900 }}>Fermeture exceptionnelle</Typography>}
                      secondary="Notification Oui/Non + dates"
                    />
                  </ListItemButton>
                  <Divider />
                  <ListItemButton
                    selected={templatesSelectedKey === 'subscription_renewal'}
                    onClick={() => setTemplatesSelectedKey('subscription_renewal')}
                  >
                    <ListItemText
                      primary={<Typography sx={{ fontWeight: 900 }}>Renouvellement abonnement</Typography>}
                      secondary="Plan + note (crée une demande en attente)"
                    />
                  </ListItemButton>
                </List>
              </Box>
            </Grid>

            <Grid item xs={12} md={8}>
              {(() => {
                const draft = getTemplateDraft(templatesSelectedKey);
                return (
                  <Box
                    sx={{
                      border: `1px solid ${theme.colors.border.light}`,
                      borderRadius: theme.borderRadius.lg,
                      backgroundColor: '#fff',
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ p: 2, borderBottom: `1px solid ${theme.colors.border.light}` }}>
                      <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Aperçu notification</Typography>
                      <Typography sx={{ fontWeight: 900, color: theme.colors.text.primary }}>{draft.notifTitle}</Typography>
                      <Typography sx={{ color: theme.colors.text.secondary, whiteSpace: 'pre-wrap' }}>{draft.notifMessage}</Typography>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <Typography sx={{ fontWeight: 900, mb: 1 }}>Aperçu email</Typography>
                      <Typography sx={{ color: theme.colors.text.secondary, mb: 1 }}>
                        Objet: <span style={{ fontWeight: 800, color: theme.colors.text.primary }}>{draft.emailSubject}</span>
                      </Typography>
                      <Box
                        sx={{
                          border: `1px solid ${theme.colors.border.light}`,
                          borderRadius: theme.borderRadius.md,
                          overflow: 'hidden',
                          backgroundColor: '#F8FAFC',
                        }}
                        dangerouslySetInnerHTML={{ __html: draft.emailHtml }}
                      />
                    </Box>
                  </Box>
                );
              })()}
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ fontWeight: 900, mb: 1 }}>Champs</Typography>

          {templatesSelectedKey === 'closure_exceptional' && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Début"
                  value={closureStart}
                  onChange={(e) => setClosureStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Fin"
                  value={closureEnd}
                  onChange={(e) => setClosureEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Motif (optionnel)"
                  value={closureReason}
                  onChange={(e) => setClosureReason(e.target.value)}
                />
              </Grid>
            </Grid>
          )}

          {templatesSelectedKey === 'subscription_renewal' && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="subscription-plan-template-label">Plan</InputLabel>
                  <Select
                    labelId="subscription-plan-template-label"
                    label="Plan"
                    value={subscriptionPlanCode}
                    onChange={(e) => setSubscriptionPlanCode(e.target.value)}
                  >
                    {subscriptionPlans.map((p) => (
                      <MenuItem key={p.id || p.code} value={p.code}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Note (optionnel)"
                  value={subscriptionNote}
                  onChange={(e) => setSubscriptionNote(e.target.value)}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplatesOpen(false)} sx={{ textTransform: 'none', fontWeight: 900 }}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={applySelectedTemplate}
            sx={{ textTransform: 'none', fontWeight: 900 }}
          >
            Appliquer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfessionalsList;
